import React, { useEffect, useState } from "react";
import {
  Button,
  Card,
  Col,
  Form,
  Input,
  Row,
  Select,
  Switch,
  Table,
  Typography,
  Modal,
  Space,
  Alert,
  Tag,
} from "antd";
import useRequest from "../hooks/useRequest";
import { Severty, ShowToast } from "../helper/toast";
import apiPath from "../constants/apiPath";
import {
  EditOutlined,
  EyeOutlined,
  DeleteOutlined,
  ExclamationCircleFilled,
  SearchOutlined,
  CalendarOutlined,
} from "@ant-design/icons";

const { Title } = Typography;
const { Option } = Select;

const AIRPORTS_JSON_URL =
  "https://raw.githubusercontent.com/mwgg/Airports/master/airports.json";

const Users = () => {
  const { request } = useRequest();

  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [form] = Form.useForm();
  const [searchForm] = Form.useForm();
  const [editingUser, setEditingUser] = useState(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [viewModalVisible, setViewModalVisible] = useState(false);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [viewingUser, setViewingUser] = useState(null);
  const [deletingUser, setDeletingUser] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const [countries, setCountries] = useState([]);
  const [airports, setAirports] = useState([]);
  const [allAirports, setAllAirports] = useState({});
  const [airportMap, setAirportMap] = useState({});
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [loadingCountries, setLoadingCountries] = useState(false);
  const [loadingAirports, setLoadingAirports] = useState(false);

  const [searchKeyword, setSearchKeyword] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [searchTimeout, setSearchTimeout] = useState(null);

  // Load countries
  const fetchCountries = async () => {
    setLoadingCountries(true);
    try {
      const res = await fetch(
        "https://restcountries.com/v3.1/all?fields=name,cca2"
      );
      const data = await res.json();
      const sorted = data
        .map((d) => ({ name: d.name.common, code: d.cca2 }))
        .sort((a, b) => a.name.localeCompare(b.name));
      setCountries(sorted);
    } catch {
      ShowToast("Failed to load countries", Severty.ERROR);
    } finally {
      setLoadingCountries(false);
    }
  };

  // Load all airports once
  const loadAllAirports = async () => {
    setLoadingAirports(true);
    try {
      const res = await fetch(AIRPORTS_JSON_URL);
      const data = await res.json();
      const iataMap = {};
      Object.values(data).forEach((a) => {
        if (a.iata) iataMap[a.iata] = a;
      });
      setAllAirports(data);
      setAirportMap(iataMap);
    } catch {
      ShowToast("Failed to load airport data", Severty.ERROR);
    } finally {
      setLoadingAirports(false);
    }
  };

  // Filter airports by country
  const fetchAirports = (countryCode) => {
    if (!countryCode || !allAirports) return setAirports([]);
    const filtered = Object.values(allAirports)
      .filter((a) => a.country === countryCode && a.iata && a.type !== "closed")
      .map((a) => ({
        iata: a.iata,
        name: a.name,
        city: a.city || "Unknown City",
        country: a.country_name || a.country,
      }))
      .sort((a, b) => a.name.localeCompare(b.name));
    setAirports(filtered);
  };

  const handleCountryChange = (value) => {
    setSelectedCountry(value);
    setAirports([]);
    form.setFieldsValue({ airport: undefined });
    const c = countries.find((c) => c.name === value);
    if (c) fetchAirports(c.code);
  };

  // Fetch Users
  const fetchUsers = (page = 1, pageSize = 10, filters = {}) => {
    setLoading(true);
    let qp = `page=${page}&page_size=${pageSize}&role=User`;
    if (filters.searchKeyword?.trim())
      qp += `&search=${encodeURIComponent(filters.searchKeyword.trim())}`;
    if (filters.status && filters.status !== "all")
      qp += `&status=${filters.status}`;
    request({
      url: `${apiPath.usersList}?${qp}`,
      method: "GET",
      onSuccess: (res) => {
        setUsers(res?.data || []);
        setPagination({
          current: res?.pagination?.current_page || 1,
          pageSize: res?.pagination?.page_size || 10,
          total: res?.pagination?.total_count || 0,
        });
        setLoading(false);
      },
      onError: () => {
        ShowToast("Failed to fetch users", Severty.ERROR);
        setLoading(false);
      },
    });
  };

  useEffect(() => {
    fetchUsers(pagination.current, pagination.pageSize);
    fetchCountries();
    loadAllAirports();
  }, []);

  const handleTableChange = (p) => {
    fetchUsers(p.current, p.pageSize, { searchKeyword, status: statusFilter });
  };

  const handleSearchChange = (e) => {
    if (searchTimeout) clearTimeout(searchTimeout);
    const t = setTimeout(() => {
      setSearchKeyword(e.target.value);
      fetchUsers(1, pagination.pageSize, {
        searchKeyword: e.target.value,
        status: statusFilter,
      });
    }, 500);
    setSearchTimeout(t);
  };

  const handleStatusFilterChange = (v) => {
    const status =
      v === "active" ? "Active" : v === "inactive" ? "Inactive" : "all";
    setStatusFilter(status);
    fetchUsers(1, pagination.pageSize, { searchKeyword, status });
  };

  const resetFilters = () => {
    setSearchKeyword("");
    setStatusFilter("all");
    searchForm.resetFields();
    if (searchTimeout) clearTimeout(searchTimeout);
    fetchUsers(1, pagination.pageSize);
  };

  const formatDate = (d) =>
    d
      ? new Date(d).toLocaleDateString("en-US", {
          year: "numeric",
          month: "short",
          day: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        })
      : "N/A";

  // Modal Open/Close
  const openModal = (user = null) => {
    setEditingUser(user);
    form.resetFields();
    setAirports([]);
    setSelectedCountry(null);
    if (user) {
      form.setFieldsValue({
        ...user,
        status: user.status === "Active",
      });
      setSelectedCountry(user.country);
      if (user.country) {
        const c = countries.find((c) => c.name === user.country);
        if (c) fetchAirports(c.code);
      }
    } else {
      form.setFieldsValue({ role: "User", status: true });
    }
    setModalVisible(true);
  };

  // ✅ Create / Update User
  const onFinish = (values) => {
    setLoading(true);
    const payload = {
      ...values,
      status: values.status ? "Active" : "Inactive",
      role: editingUser ? values.role : "User",
    };

    const req = editingUser
      ? {
          url: "/admin/update_user",
          method: "PUT",
          data: { ...payload, id: editingUser.id },
          successMsg: "User updated successfully",
          errorMsg: "Update failed",
        }
      : {
          url: "/admin/create_user",
          method: "POST",
          data: payload,
          successMsg: "User created successfully",
          errorMsg: "Create failed",
        };

    request({
      url: req.url,
      method: req.method,
      data: req.data,
      onSuccess: () => {
        ShowToast(req.successMsg, Severty.SUCCESS);
        fetchUsers(pagination.current, pagination.pageSize, {
          searchKeyword,
          status: statusFilter,
        });
        setModalVisible(false);
        setLoading(false);
      },
      onError: (e) => {
        ShowToast(e?.response?.data?.error || req.errorMsg, Severty.ERROR);
        setLoading(false);
      },
    });
  };

  // ✅ Status toggle sends "Active"/"Inactive"
  const toggleStatus = (rec) => {
    const newStatus = rec.status === "Active" ? "Inactive" : "Active";
    request({
      url: "/admin/update_status",
      method: "PUT",
      data: { id: rec.id, status: newStatus },
      onSuccess: () => {
        ShowToast("Status updated", Severty.SUCCESS);
        fetchUsers(pagination.current, pagination.pageSize, {
          searchKeyword,
          status: statusFilter,
        });
      },
      onError: (e) =>
        ShowToast(e?.response?.data?.message || "Failed", Severty.ERROR),
    });
  };

  // Delete user
  const handleDeleteUser = () => {
    if (!deletingUser) return;
    setDeleteLoading(true);
    request({
      url: "/user/delete_user",
      method: "DELETE",
      data: { id: deletingUser.id },
      onSuccess: () => {
        ShowToast("User deleted", Severty.SUCCESS);
        fetchUsers(pagination.current, pagination.pageSize, {
          searchKeyword,
          status: statusFilter,
        });
        setDeleteModalVisible(false);
        setDeleteLoading(false);
      },
      onError: (e) => {
        ShowToast(e?.response?.data?.error || "Delete failed", Severty.ERROR);
        setDeleteLoading(false);
      },
    });
  };

  const columns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      render: (t) => <strong>{t || "-"}</strong>,
    },
    { title: "Email", dataIndex: "email", key: "email" },
    { title: "Role", dataIndex: "role", key: "role" },
    {
      title: "Created",
      dataIndex: "created_at",
      key: "created_at",
      render: (d) => (
        <div style={{ fontSize: 12 }}>
          <CalendarOutlined style={{ marginRight: 4 }} />
          {formatDate(d)}
        </div>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (s, r) => (
        <Switch checked={s === "Active"} onChange={() => toggleStatus(r)} />
      ),
    },
    {
      title: "Action",
      key: "action",
      render: (_, r) => (
        <Space>
          <Button size="small" onClick={() => openModal(r)}>
            <EditOutlined />
          </Button>
          <Button
            size="small"
            onClick={() => {
              setViewingUser(r);
              setViewModalVisible(true);
            }}
          >
            <EyeOutlined />
          </Button>
          <Button danger size="small" onClick={() => setDeletingUser(r)}>
            <DeleteOutlined />
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <div className="layout-content">
      <Card>
        <Row
          justify="space-between"
          align="middle"
          style={{ marginBottom: 16 }}
        >
          <Col>
            <Title level={3}>Manage Accounts</Title>
          </Col>
          <Col>
            <Button type="primary" onClick={() => openModal()}>
              Add New User
            </Button>
          </Col>
        </Row>

        {/* Search / Filter */}
        <Card size="small" style={{ marginBottom: 16 }}>
          <Form
            form={searchForm}
            layout="vertical"
            onFinish={(v) =>
              fetchUsers(1, pagination.pageSize, {
                searchKeyword: v.keyword,
                status: statusFilter,
              })
            }
            initialValues={{ keyword: "", status: "all" }}
          >
            <Row gutter={[16, 16]} align="bottom">
              <Col xs={24} sm={12} md={8} lg={6}>
                <Form.Item label="Search Users" name="keyword">
                  <Input
                    placeholder="Name or email..."
                    prefix={<SearchOutlined />}
                    allowClear
                    onChange={handleSearchChange}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} md={6} lg={4}>
                <Form.Item label="Status" name="status">
                  <Select onChange={handleStatusFilterChange}>
                    <Option value="all">All</Option>
                    <Option value="active">Active</Option>
                    <Option value="inactive">Inactive</Option>
                  </Select>
                </Form.Item>
              </Col>
            </Row>
          </Form>
        </Card>

        {/* Table */}
        <Table
          columns={columns}
          dataSource={users}
          rowKey="id"
          loading={loading}
          pagination={pagination}
          onChange={handleTableChange}
        />
      </Card>

      {/* Add / Edit Modal */}
      <Modal
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={null}
        width={600}
      >
        <Form form={form} layout="vertical" onFinish={onFinish}>
          <Form.Item
            label="Name"
            name="name"
            rules={[{ required: true, message: "Name required" }]}
          >
            <Input placeholder="Full name" />
          </Form.Item>
          <Form.Item
            label="Email"
            name="email"
            rules={[
              { required: true, message: "Email required" },
              { type: "email", message: "Invalid email" },
            ]}
          >
            <Input placeholder="email@example.com" disabled={!!editingUser} />
          </Form.Item>
          <Form.Item label="Role" name="role">
            <Input value="User" disabled style={{ background: "#f5f5f5" }} />
          </Form.Item>
          <Form.Item
            label="Status"
            name="status"
            valuePropName="checked"
            initialValue={true}
          >
            <Switch checkedChildren="Active" unCheckedChildren="Inactive" />
          </Form.Item>
          <Form.Item>
            <Space>
              <Button onClick={() => setModalVisible(false)}>Cancel</Button>
              <Button type="primary" htmlType="submit" loading={loading}>
                {editingUser ? "Update" : "Create"} User
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Delete Modal */}
      <Modal
        open={!!deletingUser}
        onCancel={() => setDeletingUser(null)}
        onOk={handleDeleteUser}
        okText="Delete"
        okButtonProps={{ danger: true, loading: deleteLoading }}
      >
        <ExclamationCircleFilled style={{ color: "#faad14", marginRight: 8 }} />
        Are you sure you want to delete <strong>{deletingUser?.name}</strong>?
      </Modal>
    </div>
  );
};

export default Users;
